import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import MarketingShell from "./MarketingShell";
import { Plus, Upload, Trash } from "@phosphor-icons/react";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

export default function ListsPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const reload = async () => {
    setLoading(true);
    try { setRows((await api.get("/marketing/lists")).data); }
    catch { toast.error("Failed to load lists"); }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const doCreate = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/marketing/lists", { name: newName, list_type: "static_manual" });
      toast.success("List created");
      setCreateOpen(false); setNewName("");
      reload();
    } catch { toast.error("Create failed"); }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this list? Members remain as contacts.")) return;
    try { await api.delete(`/marketing/lists/${id}`); toast.success("Deleted"); reload(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <MarketingShell>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#525252]">Static lists of contacts. Created by CSV import or manually.</p>
        <div className="flex gap-2">
          <button onClick={() => nav("/marketing/imports/new")}
                  className="h-9 px-3 border border-[#E5E5E5] hover:border-[#0055FF] text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                  data-testid="import-csv-btn">
            <Upload size={12} />Import CSV
          </button>
          <button onClick={() => setCreateOpen(true)}
                  className="h-9 px-3 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                  data-testid="new-list-btn">
            <Plus size={12} weight="bold" />New List
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        {loading ? <p className="p-8 text-center text-sm text-[#A3A3A3]">Loading…</p>
        : rows.length === 0 ? <p className="p-8 text-center text-sm text-[#A3A3A3]">No lists yet.</p>
        : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-5 py-2 font-bold">Name</th>
                <th className="text-left px-3 py-2 font-bold">Type</th>
                <th className="text-right px-3 py-2 font-bold">Members</th>
                <th className="text-right px-3 py-2 font-bold">Updated</th>
                <th className="text-right px-5 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(l => (
                <tr key={l.id} className="border-t border-[#F5F5F5]" data-testid={`list-row-${l.id}`}>
                  <td className="px-5 py-3 font-semibold text-[#0A0A0A]">{l.name}</td>
                  <td className="px-3 py-3 text-xs">
                    {l.list_type === "static_csv" ? (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#EFF6FF] text-[#0055FF]">CSV Import</span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#F5F5F5] text-[#525252]">Manual</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">{l.member_count}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#A3A3A3]">{l.updated_at ? formatLocalDate(l.updated_at) : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => doDelete(l.id)} className="p-1 text-[#525252] hover:text-[#DC2626]" data-testid={`delete-list-${l.id}`}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCreateOpen(false)}>
          <div className="bg-white border border-[#E5E5E5] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-black tracking-tight mb-3">New manual list</h3>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="VIP customers"
                   className="w-full h-10 px-3 border border-[#E5E5E5] mb-4" data-testid="new-list-name" autoFocus />
            <div className="flex justify-end gap-2">
              <button onClick={() => setCreateOpen(false)} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">Cancel</button>
              <button onClick={doCreate} className="h-9 px-4 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider" data-testid="create-list-confirm">Create</button>
            </div>
          </div>
        </div>
      )}
    </MarketingShell>
  );
}
